import { ILegalClient } from '../Interfaces/ILegalClient';
import {
  ClientWarning,
  classifyClientReachability,
  fullName,
  joinNames,
  portalBlockReason,
} from '../helper/clientEmailStatus';

/** @deprecated Alias – Typ lebt jetzt zentral in helper/clientEmailStatus. */
export type DebtorWarning = ClientWarning;

/**
 * Ermittelt die Warnungen für die Zahlungsschuldner einer Rechnung.
 * Zentralisiert die Warnleisten aus #1183 (kein Schuldner) und #1185
 * (Schuldner ohne E-Mail / ohne Portalzugang / mit deaktiviertem Empfang).
 * Verwendet dieselbe Erreichbarkeits-Klassifikation wie die Mandanten-
 * Übersicht (#1198), aber mit rechnungsspezifischer Wortwahl.
 *
 * @param selectedDebtors die aufgelösten Schuldner-Objekte der Rechnung
 */
export const getDebtorWarnings = (
  selectedDebtors: ILegalClient[]
): ClientWarning[] => {
  // #1183: kein Zahlungsschuldner gewählt.
  if (selectedDebtors.length === 0) {
    return [
      {
        severity: 'warning',
        text: 'Kein Zahlungsschuldner ausgewählt – bitte einen Schuldner auswählen.',
      },
    ];
  }

  const { noEmail, noPortal, noNotification } =
    classifyClientReachability(selectedDebtors);
  const warnings: ClientWarning[] = [];

  // Fall 1 (rot): keine E-Mail-Adresse -> weder Portal noch Benachrichtigung.
  if (noEmail.length > 0) {
    const plural = noEmail.length > 1;
    warnings.push({
      severity: 'error',
      text: plural
        ? `Für ${joinNames(noEmail)} sind keine E-Mail-Adressen hinterlegt. Diese Mandanten können sich daher nicht im Mandantenportal anmelden, erhalten keine Benachrichtigungen und haben keinen Zugriff auf die Datei. Bitte hinterlegen Sie eine E-Mail-Adresse oder wählen Sie andere Zahlungsschuldner aus.`
        : `Für ${joinNames(noEmail)} ist keine E-Mail-Adresse hinterlegt. Der Mandant kann sich daher nicht im Mandantenportal anmelden, erhält keine Benachrichtigungen und hat keinen Zugriff auf die Datei. Bitte hinterlegen Sie eine E-Mail-Adresse oder wählen Sie einen anderen Zahlungsschuldner aus.`,
    });
  }

  // Fall 3 (rot): E-Mail vorhanden, aber kein Portalzugang.
  if (noPortal.length > 0) {
    const plural = noPortal.length > 1;
    const entries = noPortal
      .map(client => `${fullName(client)} (${portalBlockReason(client)})`)
      .join(', ');
    warnings.push({
      severity: 'error',
      text: plural
        ? `Die folgenden Mandanten können sich derzeit nicht im Mandantenportal anmelden und haben daher keinen Zugriff auf die Rechnung: ${entries}. Bitte prüfen Sie den Zugang zum Mandantenportal.`
        : `${entries} kann sich derzeit nicht im Mandantenportal anmelden und daher die Rechnung nicht öffnen. Bitte prüfen Sie den Zugang zum Mandantenportal.`,
    });
  }

  // Fall 2 (orange): E-Mail vorhanden, Portalzugang möglich, Empfang deaktiviert.
  if (noNotification.length > 0) {
    const plural = noNotification.length > 1;
    warnings.push({
      severity: 'warning',
      text: plural
        ? `${joinNames(noNotification)} können die Rechnung im Mandantenportal abrufen, erhalten jedoch keine Benachrichtigungen per E-Mail. Bitte aktivieren Sie den Empfang von E-Mails.`
        : `${joinNames(noNotification)} kann die Rechnung im Mandantenportal abrufen, erhält jedoch keine Benachrichtigungen per E-Mail. Bitte aktivieren Sie den Empfang von E-Mails.`,
    });
  }

  return warnings;
};
